import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ExternalLink,
  Calculator,
  Calendar,
  Building2,
  Newspaper,
  Landmark,
  FileText,
  Euro,
  AlertCircle,
  CheckCircle2,
  Clock,
} from "lucide-react";

const RisorsePage = () => {
  // Calcolatore Regime Forfettario
  const [fatturato, setFatturato] = useState("");
  const [coefficiente, setCoefficiente] = useState("78");
  const [contributi, setContributi] = useState("");
  const [risultatoCalcolo, setRisultatoCalcolo] = useState(null);

  // Calcolatore IRPEF
  const [redditoIrpef, setRedditoIrpef] = useState("");
  const [detrazioni, setDetrazioni] = useState("");
  const [risultatoIrpef, setRisultatoIrpef] = useState(null);

  // Calcolatore INPS Gestione Separata
  const [redditoInps, setRedditoInps] = useState("");
  const [tipoIscrizione, setTipoIscrizione] = useState("esclusiva");
  const [risultatoInps, setRisultatoInps] = useState(null);

  // Tab attivo per i calcolatori
  const [calcolatoreAttivo, setCalcolatoreAttivo] = useState("forfettario");

  const coefficientiATECO = [
    { value: "40", label: "Commercio (40%)" },
    { value: "54", label: "Servizi di alloggio e ristorazione (54%)" },
    { value: "62", label: "Attività professionali (62%)" },
    { value: "67", label: "Costruzioni e attività immobiliari (67%)" },
    { value: "78", label: "Professionisti (78%)" },
    { value: "86", label: "Intermediari del commercio (86%)" },
  ];

  const calcolaImposte = () => {
    const fatturatoNum = parseFloat(fatturato) || 0;
    const coeffNum = parseFloat(coefficiente) / 100;
    const contributiNum = parseFloat(contributi) || 0;

    const redditoImponibile = fatturatoNum * coeffNum;
    const redditoNetto = redditoImponibile - contributiNum;
    const impostaSostitutiva = redditoNetto > 0 ? redditoNetto * 0.15 : 0;
    const impostaAgevolata = redditoNetto > 0 ? redditoNetto * 0.05 : 0;

    setRisultatoCalcolo({
      fatturato: fatturatoNum,
      redditoImponibile,
      contributiDeducibili: contributiNum,
      redditoNetto: redditoNetto > 0 ? redditoNetto : 0,
      imposta15: impostaSostitutiva,
      imposta5: impostaAgevolata,
    });
  };

  // Calcolo IRPEF 2025
  const calcolaIrpef = () => {
    const reddito = parseFloat(redditoIrpef) || 0;
    const detrazioniNum = parseFloat(detrazioni) || 0;

    // Scaglioni IRPEF 2025
    let impostaLorda = 0;
    let dettaglioScaglioni = [];

    if (reddito <= 28000) {
      impostaLorda = reddito * 0.23;
      dettaglioScaglioni.push({ scaglione: "fino a €28.000", aliquota: "23%", importo: impostaLorda });
    } else if (reddito <= 50000) {
      const primoScaglione = 28000 * 0.23;
      const secondoScaglione = (reddito - 28000) * 0.35;
      impostaLorda = primoScaglione + secondoScaglione;
      dettaglioScaglioni.push({ scaglione: "fino a €28.000", aliquota: "23%", importo: primoScaglione });
      dettaglioScaglioni.push({ scaglione: "€28.001 - €50.000", aliquota: "35%", importo: secondoScaglione });
    } else {
      const primoScaglione = 28000 * 0.23;
      const secondoScaglione = 22000 * 0.35;
      const terzoScaglione = (reddito - 50000) * 0.43;
      impostaLorda = primoScaglione + secondoScaglione + terzoScaglione;
      dettaglioScaglioni.push({ scaglione: "fino a €28.000", aliquota: "23%", importo: primoScaglione });
      dettaglioScaglioni.push({ scaglione: "€28.001 - €50.000", aliquota: "35%", importo: secondoScaglione });
      dettaglioScaglioni.push({ scaglione: "oltre €50.000", aliquota: "43%", importo: terzoScaglione });
    }

    const impostaNetta = Math.max(0, impostaLorda - detrazioniNum);
    const aliquotaEffettiva = reddito > 0 ? (impostaNetta / reddito) * 100 : 0;

    setRisultatoIrpef({
      reddito,
      impostaLorda,
      detrazioni: detrazioniNum,
      impostaNetta,
      aliquotaEffettiva,
      dettaglioScaglioni,
    });
  };

  // Calcolo Contributi INPS Gestione Separata 2025
  const calcolaInps = () => {
    const reddito = parseFloat(redditoInps) || 0;
    
    // Aliquote 2025 Gestione Separata
    // Iscrizione esclusiva (non iscritti ad altra gestione): 26.07%
    // Iscrizione non esclusiva (già iscritti ad altra gestione o pensionati): 24%
    const aliquota = tipoIscrizione === "esclusiva" ? 0.2607 : 0.24;
    const aliquotaLabel = tipoIscrizione === "esclusiva" ? "26.07%" : "24%";
    
    // Massimale contributivo 2025: €119.650
    const massimale = 119650;
    const redditoImponibile = Math.min(reddito, massimale);
    
    const contributoTotale = redditoImponibile * aliquota;
    
    // Ripartizione: 2/3 a carico del professionista, 1/3 a carico del committente (per collaboratori)
    // Per professionisti: 100% a carico proprio ma può addebitare 4% in fattura
    const contributoAnnuo = contributoTotale;
    const contributoTrimestrale = contributoAnnuo / 4;
    const rivalsa4 = reddito * 0.04;

    setRisultatoInps({
      reddito,
      redditoImponibile,
      aliquota: aliquotaLabel,
      contributoAnnuo,
      contributoTrimestrale,
      rivalsa4,
      superaMassimale: reddito > massimale,
    });
  };

  // Scadenzario Fiscale
  const scadenze = [
    {
      data: "16 Gennaio",
      descrizione: "Versamento IVA mese precedente",
      tipo: "IVA",
      dettagli: "Versamento dell'IVA dovuta per il mese di dicembre",
    },
    {
      data: "16 Febbraio",
      descrizione: "Versamento ritenute e contributi",
      tipo: "Contributi",
      dettagli: "Versamento ritenute operate nel mese precedente",
    },
    {
      data: "16 Marzo",
      descrizione: "Saldo IVA annuale",
      tipo: "IVA",
      dettagli: "Versamento del saldo IVA risultante dalla dichiarazione annuale",
    },
    {
      data: "30 Aprile",
      descrizione: "Dichiarazione IVA",
      tipo: "Dichiarazione",
      dettagli: "Presentazione della dichiarazione IVA annuale",
    },
    {
      data: "30 Giugno",
      descrizione: "Saldo e primo acconto imposte",
      tipo: "Imposte",
      dettagli: "Versamento saldo IRPEF/IRES anno precedente e primo acconto",
    },
    {
      data: "30 Settembre",
      descrizione: "Modello 730 integrativo",
      tipo: "Dichiarazione",
      dettagli: "Termine per la presentazione del 730 integrativo",
    },
    {
      data: "31 Ottobre",
      descrizione: "Modello Redditi PF/SP/SC",
      tipo: "Dichiarazione",
      dettagli: "Presentazione dichiarazione dei redditi",
    },
    {
      data: "30 Novembre",
      descrizione: "Secondo acconto imposte",
      tipo: "Imposte",
      dettagli: "Versamento secondo o unico acconto IRPEF/IRES",
    },
    {
      data: "27 Dicembre",
      descrizione: "Acconto IVA",
      tipo: "IVA",
      dettagli: "Versamento dell'acconto IVA",
    },
  ];

  const linkUtili = [
    {
      categoria: "Istituzioni",
      links: [
        {
          nome: "Agenzia delle Entrate",
          url: "https://www.agenziaentrate.gov.it",
          descrizione: "Portale ufficiale per servizi fiscali e dichiarazioni",
          icon: Landmark,
        },
        {
          nome: "INPS",
          url: "https://www.inps.it",
          descrizione: "Servizi previdenziali e contributivi",
          icon: Building2,
        },
        {
          nome: "Camera di Commercio Udine",
          url: "https://www.ud.camcom.it",
          descrizione: "Camera di Commercio Pordenone-Udine",
          icon: Building2,
        },
        {
          nome: "Unioncamere",
          url: "https://www.unioncamere.gov.it",
          descrizione: "Unione italiana delle Camere di Commercio",
          icon: Building2,
        },
      ],
    },
    {
      categoria: "Informazione Fiscale",
      links: [
        {
          nome: "Il Sole 24 Ore - Norme e Tributi",
          url: "https://www.ilsole24ore.com/sez/norme-e-tributi",
          descrizione: "Notizie e approfondimenti fiscali",
          icon: Newspaper,
        },
        {
          nome: "Fisco Oggi",
          url: "https://www.fiscooggi.it",
          descrizione: "Rivista telematica dell'Agenzia delle Entrate",
          icon: FileText,
        },
        {
          nome: "Italia Oggi",
          url: "https://www.italiaoggi.it",
          descrizione: "Quotidiano economico, giuridico e politico",
          icon: Newspaper,
        },
      ],
    },
    {
      categoria: "Servizi Online",
      links: [
        {
          nome: "Cassetto Fiscale",
          url: "https://telematici.agenziaentrate.gov.it/Main/index.jsp",
          descrizione: "Accedi al tuo cassetto fiscale personale",
          icon: FileText,
        },
        {
          nome: "Fatture e Corrispettivi",
          url: "https://ivaservizi.agenziaentrate.gov.it/portale/",
          descrizione: "Portale fatturazione elettronica",
          icon: FileText,
        },
        {
          nome: "Registro Imprese",
          url: "https://www.registroimprese.it",
          descrizione: "Visure camerali e informazioni aziendali",
          icon: Building2,
        },
      ],
    },
  ];

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case "IVA":
        return "bg-blue-100 text-blue-700";
      case "Imposte":
        return "bg-red-100 text-red-700";
      case "Dichiarazione":
        return "bg-green-100 text-green-700";
      case "Contributi":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="min-h-screen bg-brand-background" data-testid="risorse-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-white/60 text-sm font-medium tracking-wider uppercase">
              Risorse Utili
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mt-4 mb-6">
              Strumenti e Link per la tua Attività
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Calcolatori fiscali, scadenzario adempimenti e link utili per
              gestire al meglio la tua situazione fiscale.
            </p>
          </div>
        </div>
      </section>

      {/* Calcolatori Fiscali */}
      <section className="py-16 md:py-24" data-testid="calcolatore-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              Strumenti
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-brand-foreground mt-4">
              Calcolatori Fiscali
            </h2>
            <p className="text-brand-muted mt-4 max-w-2xl mx-auto">
              Strumenti per calcolare imposte, contributi INPS e regime forfettario.
              I risultati sono puramente indicativi.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <Button
              onClick={() => setCalcolatoreAttivo("forfettario")}
              variant={calcolatoreAttivo === "forfettario" ? "default" : "outline"}
              className={`rounded-full ${calcolatoreAttivo === "forfettario" ? "bg-brand-primary text-white" : "border-brand-primary text-brand-primary"}`}
              data-testid="tab-forfettario"
            >
              Regime Forfettario
            </Button>
            <Button
              onClick={() => setCalcolatoreAttivo("irpef")}
              variant={calcolatoreAttivo === "irpef" ? "default" : "outline"}
              className={`rounded-full ${calcolatoreAttivo === "irpef" ? "bg-brand-primary text-white" : "border-brand-primary text-brand-primary"}`}
              data-testid="tab-irpef"
            >
              IRPEF 2025
            </Button>
            <Button
              onClick={() => setCalcolatoreAttivo("inps")}
              variant={calcolatoreAttivo === "inps" ? "default" : "outline"}
              className={`rounded-full ${calcolatoreAttivo === "inps" ? "bg-brand-primary text-white" : "border-brand-primary text-brand-primary"}`}
              data-testid="tab-inps"
            >
              Contributi INPS
            </Button>
          </div>

          {/* Calcolatore Regime Forfettario */}
          {calcolatoreAttivo === "forfettario" && (
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-brand-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Calculator className="text-brand-primary" size={24} />
                    Regime Forfettario
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="fatturato">Fatturato Annuo Lordo (€)</Label>
                    <Input
                      id="fatturato"
                      type="number"
                      placeholder="es. 50000"
                      value={fatturato}
                      onChange={(e) => setFatturato(e.target.value)}
                      className="bg-brand-secondary border-brand-border"
                      data-testid="input-fatturato"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coefficiente">Coefficiente di Redditività</Label>
                    <Select value={coefficiente} onValueChange={setCoefficiente}>
                      <SelectTrigger className="bg-brand-secondary border-brand-border" data-testid="select-coefficiente">
                        <SelectValue placeholder="Seleziona attività" />
                      </SelectTrigger>
                      <SelectContent>
                        {coefficientiATECO.map((coeff) => (
                          <SelectItem key={coeff.value} value={coeff.value}>
                            {coeff.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contributi">Contributi Previdenziali (€)</Label>
                    <Input
                      id="contributi"
                      type="number"
                      placeholder="es. 4000"
                      value={contributi}
                      onChange={(e) => setContributi(e.target.value)}
                      className="bg-brand-secondary border-brand-border"
                      data-testid="input-contributi"
                    />
                    <p className="text-xs text-brand-muted">Contributi INPS versati nell'anno (deducibili)</p>
                  </div>
                  <Button onClick={calcolaImposte} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white rounded-full" data-testid="btn-calcola">
                    <Calculator className="mr-2" size={18} />
                    Calcola Imposte
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-brand-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Euro className="text-brand-primary" size={24} />
                    Risultato
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {risultatoCalcolo ? (
                    <div className="space-y-4" data-testid="risultato-calcolo">
                      <div className="p-4 bg-brand-secondary rounded-lg">
                        <p className="text-sm text-brand-muted">Fatturato Lordo</p>
                        <p className="text-2xl font-semibold text-brand-foreground">€ {risultatoCalcolo.fatturato.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-4 bg-brand-secondary rounded-lg">
                        <p className="text-sm text-brand-muted">Reddito Imponibile ({coefficiente}%)</p>
                        <p className="text-2xl font-semibold text-brand-foreground">€ {risultatoCalcolo.redditoImponibile.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-4 bg-brand-secondary rounded-lg">
                        <p className="text-sm text-brand-muted">Reddito Netto (dopo contributi)</p>
                        <p className="text-2xl font-semibold text-brand-foreground">€ {risultatoCalcolo.redditoNetto.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-700">Imposta 5% (primi 5 anni)</p>
                          <p className="text-xl font-semibold text-green-800">€ {risultatoCalcolo.imposta5.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-700">Imposta 15% (ordinaria)</p>
                          <p className="text-xl font-semibold text-blue-800">€ {risultatoCalcolo.imposta15.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-xs text-amber-700">Calcolo puramente indicativo. Per una valutazione precisa contatta il nostro studio.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-brand-muted">
                      <Calculator className="mx-auto mb-4 text-brand-border" size={48} />
                      <p>Inserisci i dati e clicca "Calcola Imposte"</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Calcolatore IRPEF */}
          {calcolatoreAttivo === "irpef" && (
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-brand-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Calculator className="text-brand-primary" size={24} />
                    Calcolo IRPEF 2025
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="redditoIrpef">Reddito Imponibile Annuo (€)</Label>
                    <Input
                      id="redditoIrpef"
                      type="number"
                      placeholder="es. 35000"
                      value={redditoIrpef}
                      onChange={(e) => setRedditoIrpef(e.target.value)}
                      className="bg-brand-secondary border-brand-border"
                      data-testid="input-reddito-irpef"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="detrazioni">Detrazioni d'Imposta (€)</Label>
                    <Input
                      id="detrazioni"
                      type="number"
                      placeholder="es. 1880"
                      value={detrazioni}
                      onChange={(e) => setDetrazioni(e.target.value)}
                      className="bg-brand-secondary border-brand-border"
                      data-testid="input-detrazioni"
                    />
                    <p className="text-xs text-brand-muted">Es. detrazioni lavoro dipendente, familiari a carico, etc.</p>
                  </div>
                  <div className="p-4 bg-brand-secondary rounded-lg">
                    <p className="text-sm font-medium text-brand-foreground mb-2">Scaglioni IRPEF 2025:</p>
                    <ul className="text-xs text-brand-muted space-y-1">
                      <li>• fino a €28.000: <span className="font-medium">23%</span></li>
                      <li>• da €28.001 a €50.000: <span className="font-medium">35%</span></li>
                      <li>• oltre €50.000: <span className="font-medium">43%</span></li>
                    </ul>
                  </div>
                  <Button onClick={calcolaIrpef} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white rounded-full" data-testid="btn-calcola-irpef">
                    <Calculator className="mr-2" size={18} />
                    Calcola IRPEF
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-brand-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Euro className="text-brand-primary" size={24} />
                    Risultato IRPEF
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {risultatoIrpef ? (
                    <div className="space-y-4" data-testid="risultato-irpef">
                      <div className="p-4 bg-brand-secondary rounded-lg">
                        <p className="text-sm text-brand-muted">Reddito Imponibile</p>
                        <p className="text-2xl font-semibold text-brand-foreground">€ {risultatoIrpef.reddito.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-brand-foreground">Dettaglio per scaglioni:</p>
                        {risultatoIrpef.dettaglioScaglioni.map((s, i) => (
                          <div key={i} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                            <span className="text-brand-muted">{s.scaglione} ({s.aliquota})</span>
                            <span className="font-medium">€ {s.importo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      </div>
                      <div className="p-4 bg-brand-secondary rounded-lg">
                        <p className="text-sm text-brand-muted">Imposta Lorda</p>
                        <p className="text-2xl font-semibold text-brand-foreground">€ {risultatoIrpef.impostaLorda.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">Detrazioni applicate</p>
                        <p className="text-xl font-semibold text-green-800">- € {risultatoIrpef.detrazioni.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">Imposta Netta da Versare</p>
                        <p className="text-2xl font-semibold text-red-800">€ {risultatoIrpef.impostaNetta.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-700">Aliquota Effettiva</p>
                        <p className="text-xl font-semibold text-blue-800">{risultatoIrpef.aliquotaEffettiva.toFixed(2)}%</p>
                      </div>
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-xs text-amber-700">Calcolo semplificato. Non considera addizionali regionali/comunali.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-brand-muted">
                      <Calculator className="mx-auto mb-4 text-brand-border" size={48} />
                      <p>Inserisci i dati e clicca "Calcola IRPEF"</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Calcolatore INPS */}
          {calcolatoreAttivo === "inps" && (
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-brand-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Calculator className="text-brand-primary" size={24} />
                    Contributi INPS Gestione Separata
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="redditoInps">Reddito Imponibile Annuo (€)</Label>
                    <Input
                      id="redditoInps"
                      type="number"
                      placeholder="es. 40000"
                      value={redditoInps}
                      onChange={(e) => setRedditoInps(e.target.value)}
                      className="bg-brand-secondary border-brand-border"
                      data-testid="input-reddito-inps"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo di Iscrizione</Label>
                    <Select value={tipoIscrizione} onValueChange={setTipoIscrizione}>
                      <SelectTrigger className="bg-brand-secondary border-brand-border" data-testid="select-tipo-iscrizione">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="esclusiva">Iscrizione Esclusiva (26.07%)</SelectItem>
                        <SelectItem value="non_esclusiva">Non Esclusiva / Pensionato (24%)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-brand-muted">
                      Esclusiva: non iscritto ad altra gestione previdenziale obbligatoria
                    </p>
                  </div>
                  <div className="p-4 bg-brand-secondary rounded-lg">
                    <p className="text-sm font-medium text-brand-foreground mb-2">Aliquote 2025:</p>
                    <ul className="text-xs text-brand-muted space-y-1">
                      <li>• Iscrizione esclusiva: <span className="font-medium">26.07%</span></li>
                      <li>• Non esclusiva/pensionati: <span className="font-medium">24%</span></li>
                      <li>• Massimale: <span className="font-medium">€119.650</span></li>
                    </ul>
                  </div>
                  <Button onClick={calcolaInps} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white rounded-full" data-testid="btn-calcola-inps">
                    <Calculator className="mr-2" size={18} />
                    Calcola Contributi
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-brand-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Euro className="text-brand-primary" size={24} />
                    Risultato Contributi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {risultatoInps ? (
                    <div className="space-y-4" data-testid="risultato-inps">
                      <div className="p-4 bg-brand-secondary rounded-lg">
                        <p className="text-sm text-brand-muted">Reddito Imponibile</p>
                        <p className="text-2xl font-semibold text-brand-foreground">€ {risultatoInps.redditoImponibile.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                        {risultatoInps.superaMassimale && (
                          <p className="text-xs text-amber-600 mt-1">* Applicato massimale €119.650</p>
                        )}
                      </div>
                      <div className="p-4 bg-brand-secondary rounded-lg">
                        <p className="text-sm text-brand-muted">Aliquota Applicata</p>
                        <p className="text-2xl font-semibold text-brand-foreground">{risultatoInps.aliquota}</p>
                      </div>
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">Contributo Annuo Totale</p>
                        <p className="text-2xl font-semibold text-red-800">€ {risultatoInps.contributoAnnuo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-700">Contributo Trimestrale</p>
                        <p className="text-xl font-semibold text-blue-800">€ {risultatoInps.contributoTrimestrale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">Rivalsa 4% (addebitabile in fattura)</p>
                        <p className="text-xl font-semibold text-green-800">€ {risultatoInps.rivalsa4.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-xs text-amber-700">I contributi versati sono deducibili dal reddito ai fini IRPEF.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-brand-muted">
                      <Calculator className="mx-auto mb-4 text-brand-border" size={48} />
                      <p>Inserisci i dati e clicca "Calcola Contributi"</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Scadenzario Fiscale */}
      <section
        className="py-16 md:py-24 bg-brand-secondary"
        data-testid="scadenzario-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              Scadenze
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-brand-foreground mt-4">
              Scadenzario Adempimenti Fiscali 2025
            </h2>
            <p className="text-brand-muted mt-4 max-w-2xl mx-auto">
              Le principali scadenze fiscali da tenere a mente. Contattaci per
              non dimenticare nessun adempimento.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scadenze.map((scadenza, index) => (
              <Card
                key={index}
                className="border-brand-border bg-white"
                data-testid={`scadenza-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                        <Calendar className="text-brand-primary" size={20} />
                      </div>
                      <div>
                        <p className="font-semibold text-brand-foreground">
                          {scadenza.data}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getTipoColor(
                        scadenza.tipo
                      )}`}
                    >
                      {scadenza.tipo}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-brand-foreground mb-2">
                    {scadenza.descrizione}
                  </h3>
                  <p className="text-sm text-brand-muted">{scadenza.dettagli}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/contatti">
              <Button
                className="bg-brand-primary hover:bg-brand-primary-dark text-white rounded-full px-8"
                data-testid="scadenzario-cta"
              >
                <Clock className="mr-2" size={18} />
                Richiedi Promemoria Personalizzato
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Link Utili */}
      <section className="py-16 md:py-24" data-testid="link-utili-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              Link Utili
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-brand-foreground mt-4">
              Risorse e Siti di Riferimento
            </h2>
            <p className="text-brand-muted mt-4 max-w-2xl mx-auto">
              Una selezione di link utili per accedere rapidamente ai principali
              servizi fiscali e informativi.
            </p>
          </div>

          <div className="space-y-12">
            {linkUtili.map((categoria, catIndex) => (
              <div key={catIndex}>
                <h3 className="font-heading text-xl font-semibold text-brand-foreground mb-6 flex items-center gap-2">
                  <CheckCircle2 className="text-brand-primary" size={20} />
                  {categoria.categoria}
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoria.links.map((link, linkIndex) => (
                    <a
                      key={linkIndex}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                      data-testid={`link-${catIndex}-${linkIndex}`}
                    >
                      <Card className="border-brand-border h-full transition-all duration-300 hover:border-brand-primary hover:shadow-lg hover:-translate-y-1">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-primary/10 transition-colors">
                              <link.icon
                                className="text-brand-primary"
                                size={24}
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-brand-foreground group-hover:text-brand-primary transition-colors">
                                  {link.nome}
                                </h4>
                                <ExternalLink
                                  size={14}
                                  className="text-brand-muted"
                                />
                              </div>
                              <p className="text-sm text-brand-muted mt-1">
                                {link.descrizione}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-primary">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-white mb-4">
            Hai bisogno di assistenza personalizzata?
          </h2>
          <p className="text-white/80 mb-8">
            I nostri strumenti sono indicativi. Per una consulenza precisa e
            personalizzata, contatta il nostro studio.
          </p>
          <Link to="/contatti">
            <Button
              size="lg"
              className="bg-white text-brand-primary hover:bg-white/90 rounded-full px-10"
              data-testid="risorse-final-cta"
            >
              Contattaci Ora
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RisorsePage;
